import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import ImageUploader from '../../components/ImageUploader';
import VideoUploader from '../../components/VideoUploader';
import PinsMap from '../../components/PinsMap';
import AddressFields from '../../components/AddressFields';
import { PartnerCard } from '../../components/Cards';
import {
  Button, Chip, Input, Loading, Row,
} from '../../components/Ui';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { currency } from '../../utils/format';
import { SERVICE_MODES, SERVICE_SLOTS, VEHICLE_ICON } from '../../constants';

const iso = (d) => new Date(d).toISOString().slice(0, 10);

/** Book a service: the bike, the work, how it gets there, and what is wrong. */
const BookBikeServicePage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { location } = useApp();
  const [params] = useSearchParams();
  // Cars and two-wheelers share the page; the family picks vehicles, price list and garages.
  const isCar = params.get('family') === 'car';
  const family = isCar ? 'car' : 'two';

  const [services, setServices] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [garages, setGarages] = useState([]);
  const [showMap, setShowMap] = useState(false);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);

  const [vehicleId, setVehicleId] = useState('');
  const [chosen, setChosen] = useState(params.get('service') ? [params.get('service')] : []);
  const [partnerId, setPartnerId] = useState('');
  const [mode, setMode] = useState('garage');
  const [day, setDay] = useState(iso(new Date(Date.now() + 86400000)));
  const [slot, setSlot] = useState(SERVICE_SLOTS[0]);
  const [km, setKm] = useState('');
  const [problem, setProblem] = useState({ text: '', photos: [], video: '', voiceNote: '' });
  const [address, setAddress] = useState({ line1: '', landmark: '', city: location.city || '', pincode: '' });

  const load = useCallback(() => {
    api.garage.services({ forKind: isCar ? 'car' : undefined }).then((r) => setServices(r.data || [])).catch(() => setServices([]));
    api.garage.vehicles()
      .then((r) => {
        const mine = (r.data || []).filter((v) => (v.kind === 'car') === isCar);
        setVehicles(mine);
        if (mine.length) {
          setVehicleId(mine[0]._id);
          setKm(String(mine[0].currentKm || ''));
        }
      })
      .catch(() => setVehicles([]))
      .finally(() => setLoading(false));
    // Only garages that work on this kind of vehicle - by the category they signed up under.
    api.garage.garages({ lat: location.latitude, lng: location.longitude, radius: 25, vehicleKind: isCar ? 'car' : 'bike' })
      .then((r) => setGarages(r.data || []))
      .catch(() => setGarages([]));
  }, [location.latitude, location.longitude, isCar]);
  useEffect(load, [load]);

  const toggleService = (id) => setChosen((list) => (list.includes(id) ? list.filter((s) => s !== id) : [...list, id]));
  const total = services.filter((s) => chosen.includes(s._id)).reduce((n, s) => n + (s.fromPrice || 0), 0);

  const confirm = async () => {
    if (!vehicleId) { navigate(`/my-vehicles-service?family=${family}&add=1`); return; }
    if (!chosen.length) { toast.error('Choose at least one thing to be done.', 'Pick a service'); return; }
    setBooking(true);
    try {
      const res = await api.garage.book({
        vehicleId,
        partnerId: partnerId || undefined,
        serviceIds: chosen,
        mode,
        scheduledDate: day,
        scheduledSlot: slot,
        currentKm: Number(km) || undefined,
        problem,
        address: mode === 'garage' ? {} : {
          ...address,
          // The pickup point: the exact GPS fix if they used it, else the header location.
          coordinates: [address.longitude || location.longitude, address.latitude || location.latitude],
        },
      });
      navigate(`/service-jobs/${res.data._id}`);
    } catch (err) {
      toast.error(err.message, 'Could not book');
    } finally { setBooking(false); }
  };

  if (loading) return <Loading text="Loading services…" />;

  return (
    <div style={{ maxWidth: 860 }}>
      <PageHeader title="Book a service" subtitle={isCar ? 'Car' : 'Two-wheeler'} back />

      <div className="card sh-sm">
        <div className="t-title">Which vehicle?</div>
        {!vehicles.length ? (
          <Button title={isCar ? '+ Add your car' : '+ Add your bike or scooter'} variant="ghost" onClick={() => navigate(`/my-vehicles-service?family=${family}&add=1`)} />
        ) : (
          <div className="row wrap" style={{ gap: 10, margin: '10px 0' }}>
            {vehicles.map((v) => (
              <button
                type="button"
                key={v._id}
                className="card"
                style={{ cursor: 'pointer', borderColor: vehicleId === v._id ? 'var(--primary)' : undefined, width: 180, textAlign: 'left' }}
                onClick={() => { setVehicleId(v._id); setKm(String(v.currentKm || '')); }}
              >
                {v.photo ? (
                  <img src={fileUrl(v.photo)} alt={v.model} style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover' }} />
                ) : <span style={{ fontSize: 22 }}>{VEHICLE_ICON[v.kind] || '🏍️'}</span>}
                <div className="t-small">{v.brand} {v.model}</div>
                <div className="t-tiny">{v.regNumber || `${v.currentKm} km`}</div>
              </button>
            ))}
          </div>
        )}

        <Input label="Current km on the meter" inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value.replace(/\D/g, ''))} />

        <div className="t-title" style={{ marginTop: 14 }}>What needs doing?</div>
        <div className="row wrap" style={{ gap: 8, margin: '10px 0' }}>
          {services.map((s) => (
            <Chip
              key={s._id}
              label={`${s.icon} ${s.title} · ₹${s.fromPrice}+`}
              active={chosen.includes(s._id)}
              onClick={() => toggleService(s._id)}
            />
          ))}
        </div>

        <div className="t-title" style={{ marginTop: 14 }}>How do you want it done?</div>
        <div className="split-3" style={{ gap: 12, margin: '10px 0' }}>
          {SERVICE_MODES.map((m) => (
            <button
              type="button"
              key={m.value}
              className="card"
              style={{ cursor: 'pointer', textAlign: 'left', borderColor: mode === m.value ? 'var(--primary)' : undefined }}
              onClick={() => setMode(m.value)}
            >
              <div className="t-title">{m.label}</div>
              <div className="t-tiny">{m.hint}</div>
            </button>
          ))}
        </div>

        {mode !== 'garage' ? (
          <AddressFields value={address} onChange={setAddress} fields={['line1', 'landmark', 'city']} onError={(msg) => toast.error(msg)} />
        ) : null}

        {garages.length ? (
          <>
            <div className="row-between" style={{ marginTop: 14 }}>
              <div className="t-title">Garage</div>
              <Chip label={showMap ? '☰ List' : '🗺️ View on map'} onClick={() => setShowMap((v) => !v)} />
            </div>
            <div style={{ margin: '10px 0' }}>
              <Chip label="Any nearby garage" active={!partnerId} onClick={() => setPartnerId('')} />
            </div>
            {showMap ? (
              <PinsMap
                height={320}
                me={location.longitude ? [location.longitude, location.latitude] : null}
                points={garages.map((g) => ({ id: g._id, label: g.businessName, icon: '🔧', coordinates: g.location && g.location.coordinates }))}
                selectedId={partnerId}
                onSelect={setPartnerId}
              />
            ) : null}
            {/* Clicking a pin picks that garage; its card is shown right under the map. */}
            <div className="grid grid-list" style={{ marginTop: 10 }}>
              {(showMap ? garages.filter((g) => g._id === partnerId) : garages).map((g) => (
                <PartnerCard
                  key={g._id}
                  partner={g}
                  onClick={() => setPartnerId(partnerId === g._id && !showMap ? '' : g._id)}
                  style={partnerId === g._id ? { outline: '2px solid var(--primary)' } : undefined}
                />
              ))}
            </div>
            {showMap && !partnerId ? <div className="t-tiny" style={{ marginTop: 8 }}>Click a pin to choose that garage, or leave it to us to pick the nearest.</div> : null}
          </>
        ) : null}

        <div className="split-2" style={{ gap: 12 }}>
          <Input label="Date" type="date" value={day} min={iso(new Date())} onChange={(e) => setDay(e.target.value)} />
          <div>
            <label className="field-label">Time</label>
            <div className="row wrap" style={{ gap: 8 }}>
              {SERVICE_SLOTS.map((s) => <Chip key={s} label={s} active={slot === s} onClick={() => setSlot(s)} />)}
            </div>
          </div>
        </div>

        <div className="t-title" style={{ marginTop: 14 }}>What is the problem?</div>
        <Input
          label="Tell us in your own words"
          placeholder="Bike start nahi ho rahi…"
          textarea
          rows={3}
          value={problem.text}
          onChange={(e) => setProblem({ ...problem, text: e.target.value })}
        />
        <ImageUploader
          label="Photos of the problem"
          hint="A close-up helps the mechanic come prepared."
          folder="service"
          max={6}
          value={problem.photos}
          onChange={(v) => setProblem({ ...problem, photos: v })}
        />
        <VideoUploader
          label="Video (optional)"
          hint="Record the sound or the fault if it is easier to show."
          folder="service"
          value={problem.video}
          onChange={(v) => setProblem({ ...problem, video: v })}
        />

        <div className="card" style={{ marginTop: 14, background: 'var(--bg)' }}>
          <Row label="Services chosen" value={String(chosen.length)} />
          <Row label="Starting from" value={currency(total)} />
          <div className="t-tiny">
            The garage inspects the vehicle first and sends an estimate. Nothing extra is done until you approve it.
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          <Button title={`Confirm booking · from ${currency(total)}`} loading={booking} onClick={confirm} full />
        </div>
      </div>
    </div>
  );
};

export default BookBikeServicePage;
